import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { BRAND, NAV_LINKS } from "@/lib/content";
import { trackCustomEvent } from "@/lib/metaPixel";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { LogoLockup } from "./LogoLockup";
import { Link } from "react-router-dom";

export const Nav = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled ? "border-slate-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70" : "border-transparent bg-white"
      }`}
    >
      <div className="mx-auto flex h-28 w-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <LogoLockup size="md" />

        <nav className="hidden flex-1 items-center justify-center gap-4 md:flex lg:gap-5" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `relative whitespace-nowrap text-sm font-medium transition-colors ${
                  isActive ? "text-blue-600" : "text-slate-700 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {t(l.key)}
                  {isActive && <span className="absolute -bottom-1 left-0 h-[2px] w-full rounded-full bg-blue-600" />}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2.5 md:flex">
          <span className="hidden items-center gap-1.5 whitespace-nowrap text-xs font-medium text-slate-500 xl:inline-flex">
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> {t("nav.hkIncorporated")}
          </span>
          <LanguageSwitcher />
          <Link
            to="/contact"
            data-testid="nav-professional-inquiry-button"
            onClick={() => trackCustomEvent("GetAccessClick", { location: "nav_desktop" })}
          >
            <Button className="h-10 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-white hover:bg-blue-700">
              {t("nav.professionalInquiry")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher variant="mobile" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-nav-open-button"
                aria-label="Open menu"
                className="grid h-10 w-10 place-items-center rounded-md border border-slate-200 bg-white text-slate-900"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" data-testid="mobile-nav-sheet" className="w-[92%] max-w-sm overflow-y-auto border-l border-slate-200 bg-white p-6">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex items-center justify-between">
                <LogoLockup size="sm" />
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="grid h-10 w-10 place-items-center rounded-md border border-slate-200">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile">
                {NAV_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                    className={({ isActive }) =>
                      `rounded-lg px-3 py-3 text-base font-semibold transition-colors ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-800 hover:bg-slate-50"}`
                    }
                  >
                    {t(l.key)}
                  </NavLink>
                ))}
              </nav>
              <div className="mt-8 rounded-xl border border-slate-200 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700">
                  <ShieldCheck className="h-4 w-4" /> {t("brand.complianceLine")}
                </div>
              </div>
              <Link
                to="/contact"
                data-testid="mobile-sticky-professional-inquiry-button"
                className="mt-6 block"
                onClick={() => {
                  setOpen(false);
                  trackCustomEvent("GetAccessClick", { location: "nav_mobile" });
                }}
              >
                <Button className="h-11 w-full rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                  {t("nav.professionalInquiry")} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <p className="mt-4 text-center text-[10px] uppercase tracking-[0.2em] text-slate-400">{BRAND.marketingLine}</p>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
